import { createFileRoute, Link, notFound, redirect } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { CTASection } from "@/components/cta-section";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { LocationArticle } from "@/components/location-article";
import { LocationMap } from "@/components/location-map";
import { LocationFactsTable } from "@/components/location-facts-table";
import { countyFacts } from "@/lib/location-facts";
import { buildHead, breadcrumbSchema, faqSchema, serviceSchema, BASE } from "@/lib/seo";
import { countyIndexPolicy } from "@/lib/location-indexing";
import { CityPage } from "@/components/city-page";
import { buildCityPageData, cityPageHead } from "@/lib/city-page-data";

import { findCounty, findFlatCity, cityLinkProps, isSoleCountyOfRegion, type CountySummary } from "@/lib/locations";
import { renderSpintax } from "@/lib/spintax";
import {
  COUNTY_HERO_TITLE_TEMPLATE,
  COUNTY_HERO_INTRO_TEMPLATE,
  COUNTY_BODY_TEMPLATE,
  COUNTY_META_TITLE_TEMPLATE,
  COUNTY_META_DESCRIPTION_TEMPLATE,
} from "@/content/county-template";

/** Extract H3 questions + following body from rendered markdown for FAQPage JSON-LD. */
function extractFaqs(body: string): { question: string; answer: string }[] {
  const lines = body.split("\n");
  const faqs: { question: string; answer: string }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^###\s+(.+)$/);
    if (!m) continue;
    const parts: string[] = [];
    for (let j = i + 1; j < lines.length; j++) {
      const l = lines[j];
      if (/^#{1,6}\s/.test(l)) break;
      if (l.trim()) parts.push(l.trim());
    }
    if (parts.length) faqs.push({ question: m[1].trim(), answer: parts.join(" ") });
  }
  return faqs;
}

export const Route = createFileRoute("/hr-outsourcing-services/$region/$county/")({
  loader: ({ params }) => {
    // Legacy city slugs that were retired to remove head-term duplication.
    const RETIRED_CITY_SLUGS: Record<string, string> = { "london/london": "city-of-london" };
    const retired = RETIRED_CITY_SLUGS[`${params.region}/${params.county}`];
    if (retired) {
      throw redirect({
        to: "/hr-outsourcing-services/$region/$county",
        params: { region: params.region, county: retired },
        statusCode: 301,
      });
    }
    // A county named after its own region has no page of its own — its hub is
    // the region page, and its towns sit one level up in the URL.
    const flatCity = findFlatCity(params.region, params.county);
    if (flatCity) return { kind: "city" as const, city: buildCityPageData(flatCity) };
    if (params.county === params.region || isSoleCountyOfRegion(params.region, params.county)) {
      throw redirect({ to: "/hr-outsourcing-services/$region", params: { region: params.region }, statusCode: 301 });
    }
    const county = findCounty(params.region, params.county);
    if (!county) throw notFound();
    const seed = `county:${county.regionSlug}/${county.countySlug}`;
    const topCities = county.cities.slice(0, 3).map((c) => c.city);
    const nearbyList =
      topCities.length === 0
        ? county.region
        : topCities.length === 1
          ? topCities[0]
          : topCities.length === 2
            ? `${topCities[0]} and ${topCities[1]}`
            : `${topCities[0]}, ${topCities[1]} and ${topCities[2]}`;
    const seenLgas = new Set<string>();
    const totalBusinesses = county.cities.reduce((sum, c) => {
      if (seenLgas.has(c.lga)) return sum;
      seenLgas.add(c.lga);
      return sum + (c.businesses || 0);
    }, 0);
    // Largest town by registered businesses — used as the county's anchor
    // centre in the copy rather than an arbitrary alphabetical first entry.
    const topTown = [...county.cities].sort((a, b) => b.businesses - a.businesses)[0]?.city ?? county.county;
    const tokens: Record<string, string> = {
      County: county.county,
      Region: county.region,
      TownCount: String(county.cities.length),
      BusinessCount: totalBusinesses.toLocaleString("en-GB"),
      NearbyList: nearbyList,
      TopTown: topTown,
    };
    // Single-town counties otherwise render "1 towns and cities".
    const tidy = (s: string) =>
      s
        .replace(/\b1 towns and cities\b/g, "1 town or city")
        .replace(/\b1 (locations|Towns)\b/g, (_m, w: string) => (w === "locations" ? "1 location" : "1 Town"))
        .replace(/[ \t]{2,}/g, " ");
    return {
      kind: "county" as const,
      county,
      heroTitle: tidy(renderSpintax(COUNTY_HERO_TITLE_TEMPLATE, seed + ":ht", tokens)),
      heroIntro: tidy(renderSpintax(COUNTY_HERO_INTRO_TEMPLATE, seed + ":hi", tokens)),
      body: tidy(renderSpintax(COUNTY_BODY_TEMPLATE, seed + ":body", tokens)),
      metaTitle: tidy(renderSpintax(COUNTY_META_TITLE_TEMPLATE, seed + ":mt", tokens)),
      metaDescription: tidy(renderSpintax(COUNTY_META_DESCRIPTION_TEMPLATE, seed + ":md", tokens)),
    };

  },
  head: ({ loaderData }) => {
    if (!loaderData) return { meta: [{ title: "County unavailable" }, { name: "robots", content: "noindex" }] };
    if (loaderData.kind === "city") return cityPageHead(loaderData.city);
    const { county, metaTitle, metaDescription, body } = loaderData;
    const path = `/hr-outsourcing-services/${county.regionSlug}/${county.countySlug}`;
    const faqs = extractFaqs(body);
    return buildHead({
      title: metaTitle,
      description: metaDescription,
      path,
      robots: countyIndexPolicy(county).robots,

      schemas: [
        serviceSchema({
          name: `HR Outsourcing in ${county.county}`,
          description: metaDescription,
          serviceType: "HR Outsourcing",
        }),
        {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${BASE}${path}#webpage`,
          url: `${BASE}${path}`,
          name: metaTitle,
          description: metaDescription,
          inLanguage: "en-GB",
          about: {
            "@type": "AdministrativeArea",
            name: `${county.county}, ${county.region}, United Kingdom`,
          },
        },
        faqs.length > 0 ? faqSchema(faqs) : null,
        breadcrumbSchema([
          { label: "Home", path: "/" },
          { label: "Locations", path: "/hr-outsourcing-services" },
          { label: county.region, path: `/hr-outsourcing-services/${county.regionSlug}` },
          { label: county.county, path },
        ]),
      ].filter(Boolean),
    });
  },
  notFoundComponent: CountyNotFound,
  component: CountyHub,
});


function CountyNotFound() {
  return (
    <div className="mx-auto max-w-2xl px-6 py-24 text-center">
      <h1 className="font-serif text-3xl text-brand">County not yet live</h1>
      <p className="mt-4 text-muted-foreground">This county is being prepared for a future release phase.</p>
      <Link to="/hr-outsourcing-services" className="mt-6 inline-block text-accent underline">
        See all live regions
      </Link>
    </div>
  );
}

function CountyHub() {
  const data = Route.useLoaderData();
  if (data.kind === "city") return <CityPage data={data.city} />;
  const { county, heroTitle, heroIntro, body } = data as {
    kind: "county";
    county: CountySummary;
    heroTitle: string;
    heroIntro: string;
    body: string;
  };
  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        eyebrow={`${county.region} · County`}
        title={heroTitle}
        subtitle={`Fixed-fee HR support across ${county.county}`}
        intro={heroIntro}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Locations", path: "/hr-outsourcing-services" },
          { label: county.region, path: `/hr-outsourcing-services/${county.regionSlug}` },
          { label: county.county, path: `/hr-outsourcing-services/${county.regionSlug}/${county.countySlug}` },
        ]}
        actions={
          <Link
            to="/contact-hr-experts"
            className="inline-flex items-center justify-center rounded-md bg-accent px-5 py-2.5 text-sm font-medium text-accent-foreground hover:brightness-110"
          >
            Request a call-back
          </Link>
        }
      />
      <LocationFactsTable
        place={county.county}
        note={`Coverage, employer density and typical fixed-fee retainer across ${county.county}.`}
        facts={countyFacts(county.county, county.cities)}
      />
      <LocationArticle
        body={body}
        location={county.county}
        accreditations={<AccreditationsStrip />}
        mapSlot={
          <LocationMap
            query={`${county.county}, ${county.region}, United Kingdom`}
            label={county.county}
          />
        }
      />

      {/* Towns comparison table, gives the hub extractable data rather than
          a bare link grid. */}
      <section className="bg-muted/30 px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-5xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-accent">Towns &amp; cities</p>
          <h2 className="mt-3 font-serif text-3xl leading-tight text-brand sm:text-4xl">
            Compare HR support across {county.county}
          </h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            {county.cities.length} location{county.cities.length === 1 ? "" : "s"} covered in {county.county}.
            Open the town where your people actually work for local pricing and sector detail.
          </p>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-card">
            <table className="w-full min-w-[32rem] border-collapse text-left text-sm">
              <caption className="sr-only">
                Towns and cities covered by HR outsourcing services in {county.county}
              </caption>
              <thead>
                <tr className="border-b border-border bg-muted/60 text-[11px] uppercase tracking-widest text-muted-foreground">
                  <th scope="col" className="px-5 py-3 font-semibold">Town or city</th>
                  <th scope="col" className="px-5 py-3 font-semibold">Type</th>
                  <th scope="col" className="px-5 py-3 font-semibold">Local authority</th>
                </tr>
              </thead>
              <tbody>
                {[...county.cities]
                  .sort((a, b) => b.businesses - a.businesses || a.city.localeCompare(b.city))
                  .map((c) => (
                    <tr key={c.citySlug} className="border-b border-border/60 last:border-0 hover:bg-muted/40">
                      <th scope="row" className="px-5 py-3 font-normal">
                        <Link
                          {...cityLinkProps(c)}
                          className="font-medium text-brand underline-offset-4 hover:text-accent hover:underline"
                        >
                          HR outsourcing in {c.city}
                        </Link>
                      </th>
                      <td className="px-5 py-3 text-muted-foreground">{c.tier}</td>
                      <td className="px-5 py-3 text-muted-foreground">{c.lga}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>


      <CTASection />
    </>
  );
}
